/**
 * FacebookFeedWidget · public-facing "Latest from Facebook" card grid.
 *
 * Pulls up to 6 recent posts from the Karine Lauzon FB Page and renders
 * them as a 3-column masonry (2-col on tablet, 1-col on mobile) so
 * clients who don't use Facebook can still see this week's clinic
 * moments, promotions and Tanya-style announcements.
 *
 * Design choices:
 *   • Silent-fail — if the API returns 0 posts (Meta rate limit, DNS
 *     hiccup, expired token) the widget renders NOTHING. No skeleton,
 *     no error card, no attention pulled to a broken section.
 *   • Each card links to the original FB post so likes/shares still
 *     flow back to Karine's page.
 *   • Loads lazily via IntersectionObserver — we don't waste a Graph
 *     API call until the widget is actually scrolled into view.
 */
import { useEffect, useRef, useState } from "react";
import { Facebook, ExternalLink } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Format an ISO date as "Jan 14 · 3:20 PM"
function _formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch (_) {
    return "";
  }
}

export default function FacebookFeedWidget({
  limit = 6,
  title = "Latest from Facebook",
  subtitle = "This week's moments from our Ottawa · Kanata · Carleton Place clinics.",
  className = "",
}) {
  const [posts, setPosts] = useState([]);
  const [state, setState] = useState("idle"); // idle | loading | loaded | error
  const rootRef = useRef(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!rootRef.current) return;
    const io = new IntersectionObserver(async (entries) => {
      const inView = entries.some((e) => e.isIntersecting);
      if (!inView || fetchedRef.current) return;
      fetchedRef.current = true;
      setState("loading");
      try {
        const res = await fetch(`${API_BASE}/api/facebook/recent-posts?limit=${limit}`, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data && Array.isArray(data.posts) && data.posts.length > 0) {
          setPosts(data.posts);
          setState("loaded");
        } else {
          setState("error");
        }
      } catch (_) {
        setState("error");
      }
    }, { rootMargin: "300px" });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [limit]);

  // Silent-fail — never render a broken section.
  if (state === "error" || (state === "loaded" && posts.length === 0)) {
    return <div ref={rootRef} className="hidden" aria-hidden />;
  }

  return (
    <section
      ref={rootRef}
      className={`py-14 sm:py-20 bg-gradient-to-b from-white via-zinc-50 to-white ${className}`}
      data-testid="facebook-feed-widget"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4 mb-8 sm:mb-12">
          <div>
            <span className="inline-flex items-center gap-2 text-[#1877F2] text-[10px] sm:text-xs uppercase tracking-[0.3em] font-semibold mb-2">
              <Facebook className="w-3.5 h-3.5" />
              Facebook · Live Feed
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-zinc-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {title}
            </h2>
            <p className="text-zinc-600 text-sm sm:text-base mt-2 max-w-2xl">
              {subtitle}
            </p>
          </div>
          <a
            href="https://www.facebook.com/karinelauzon.rn"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-[#1877F2] font-medium whitespace-nowrap"
            data-testid="fb-feed-follow-link"
          >
            Follow us
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {state === "loading" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        )}

        {state === "loaded" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => (
              <a
                key={p.id}
                href={p.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                data-testid={`fb-post-${p.id}`}
              >
                {p.image_url && (
                  <div className="aspect-square overflow-hidden bg-zinc-100">
                    <img
                      src={p.image_url}
                      alt={p.title || "Karine Lauzon Facebook post"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-4 sm:p-5">
                  <p className="text-[10px] uppercase tracking-widest text-[#1877F2] font-bold mb-1.5 flex items-center gap-1.5">
                    <Facebook className="w-3 h-3" />
                    {_formatWhen(p.timestamp)}
                  </p>
                  <h3 className="text-zinc-900 font-semibold text-sm sm:text-base leading-snug mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {p.title}
                  </h3>
                  {p.sub && (
                    <p className="text-zinc-600 text-xs sm:text-sm leading-relaxed line-clamp-3">
                      {p.sub}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-3 text-xs text-zinc-500 group-hover:text-[#1877F2] font-medium">
                    Read on Facebook
                    <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="mt-8 sm:mt-10 text-center sm:hidden">
          <a
            href="https://www.facebook.com/karinelauzon.rn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1877F2] text-white text-sm font-medium hover:bg-[#0d5eca] transition-colors"
            data-testid="fb-feed-follow-btn-mobile"
          >
            <Facebook className="w-4 h-4" />
            Follow Karine Lauzon RN on Facebook
          </a>
        </div>
      </div>
    </section>
  );
}
